import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/PageHeader'
import {
  Button,
  Card,
  Chip,
  ConfirmDeleteModal,
  EmptyState,
  Input,
  Modal,
  SearchableSelect,
  Skeleton,
} from '../components/ui'
import {
  useAccessGroups,
  useAllDirectoryGrants,
  useDirectoryGroupEffectiveAccess,
  useGroup,
  useGroupOccupancies,
  useGroups,
  useGroupsTree,
  useLocationTree,
  useMergeGroup,
  usePersons,
} from '../api/hooks'
import { api } from '../api/client'
import { useAuthStore } from '../stores/auth'
import type { GroupKind, GroupOccupancy, Person } from '../api/types'
import { SelectField, personStatusTone } from './Directory'
import { AddOccupancyModal } from './PersonDetail'
import { LocationLink } from '../components/LocationLink'
import { locationPaths } from '../lib/locations'
import { GROUP_KINDS, GroupParentSelect, findGroupNode, groupKindLabel, groupSubtreeIds } from './Groups'
import { readOnlyReason } from '../lib/dryrun'
import { MergeModal } from '../components/features/MergeModal'

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** PUT /persons is a full replace — resend every field with the new groups. */
function personBody(person: Person, groups: string[]) {
  return {
    first_name: person.first_name,
    last_name: person.last_name,
    external_ref: person.external_ref,
    emails: person.emails,
    phones: person.phones,
    status: person.status,
    groups,
    valid_from: person.valid_from,
    valid_to: person.valid_to,
  }
}

function AddMemberModal({
  open,
  groupUid,
  existing,
  onClose,
}: {
  open: boolean
  /** Directory-group uid the picked person is attached to. */
  groupUid: string
  /** Person uids already in this group (excluded from the picker). */
  existing: Set<string>
  onClose: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [personUid, setPersonUid] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  const persons = usePersons(q)

  const attach = useMutation({
    mutationFn: () => {
      const person = (persons.data ?? []).find((p) => p.unique_id === personUid)
      if (!person) return Promise.reject(new Error('Select a person'))
      return api.updatePerson(person.unique_id, personBody(person, [...person.groups, groupUid]))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope] })
      onClose()
    },
  })

  useEffect(() => {
    if (open) {
      setSearch('')
      setQ('')
      setPersonUid('')
      attach.reset()
    }
    // attach.reset is stable; only reset when the modal (re)opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const personOptions = useMemo(
    () =>
      (persons.data ?? [])
        .filter((p) => !existing.has(p.unique_id))
        .map((p) => ({ value: p.unique_id, label: `${p.display_name} · ${p.unique_id}` })),
    [persons.data, existing],
  )

  return (
    <Modal open={open} title="Add member" onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (personUid) attach.mutate()
        }}
      >
        <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
          The person joins this group and inherits its access grants.
        </p>
        <SearchableSelect
          label="Person"
          value={personUid}
          onChange={(v) => {
            setPersonUid(v)
            // Keep search in sync so the same query continues to filter if the
            // operator reopens the dropdown.
            const picked = personOptions.find((o) => o.value === v)
            if (picked) setSearch(picked.label.split(' · ')[0])
          }}
          options={personOptions}
          placeholder="— select person —"
          searchPlaceholder="Search name, email, phone…"
          loading={persons.isPending}
          emptyText={persons.isPending ? 'Searching…' : 'No people to add — everyone matching is already a member.'}
        />
        {attach.isError && <p className="text-xs text-ostrel-danger">{errText(attach.error)}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" type="submit" disabled={attach.isPending || !personUid}>
            {attach.isPending ? 'Adding…' : 'Add member'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function AddAccessGrantModal({
  open,
  groupUid,
  taken,
  onClose,
}: {
  open: boolean
  /** Directory-group uid receiving the grant. */
  groupUid: string
  /** Access-group uids already granted to this group (the API rejects duplicates). */
  taken: Set<string>
  onClose: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const accessGroups = useAccessGroups()

  const [accessGroup, setAccessGroup] = useState('')

  const create = useMutation({
    mutationFn: () => api.createDirectoryGrant(accessGroup, groupUid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope, 'directory-grants'] })
      queryClient.invalidateQueries({ queryKey: [scope, 'access-groups'] })
      queryClient.invalidateQueries({ queryKey: [scope, 'effective-access'] })
      onClose()
    },
  })

  useEffect(() => {
    if (open) {
      setAccessGroup('')
      create.reset()
    }
    // create.reset is stable; only reset when the modal (re)opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const options = (accessGroups.data ?? [])
    .filter((g) => !taken.has(g.unique_id))
    .map((g) => ({ value: g.unique_id, label: g.name }))

  return (
    <Modal open={open} title="Grant access group" onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (accessGroup) create.mutate()
        }}
      >
        <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
          Every active person in this group — or any group below it — becomes a member of the
          access group.
        </p>
        <SearchableSelect
          label="Access group"
          value={accessGroup}
          onChange={setAccessGroup}
          options={options}
          placeholder="— select access group —"
          searchPlaceholder="Search access groups…"
          loading={accessGroups.isPending}
          emptyText="Every access group is already granted to this group."
        />
        {create.isError && <p className="text-xs text-ostrel-danger">{errText(create.error)}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" type="submit" disabled={create.isPending || !accessGroup || options.length === 0}>
            {create.isPending ? 'Granting…' : 'Grant'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default function GroupDetailPage() {
  const { uid = '' } = useParams()
  const navigate = useNavigate()
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const groupQuery = useGroup(uid)
  const tree = useGroupsTree()
  const persons = usePersons()
  const effective = useDirectoryGroupEffectiveAccess(uid)
  const accessGroups = useAccessGroups()
  const allGrants = useAllDirectoryGrants()
  const occupancies = useGroupOccupancies(uid)
  const locationTree = useLocationTree()
  const locPaths = useMemo(
    () => (locationTree.data ? locationPaths(locationTree.data) : new Map<string, string>()),
    [locationTree.data],
  )

  const group = groupQuery.data

  const [name, setName] = useState('')
  const [kind, setKind] = useState<GroupKind>('organisation')
  const [parent, setParent] = useState('')
  const [externalRef, setExternalRef] = useState('')
  const [inheritsGrants, setInheritsGrants] = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [addMemberOpen, setAddMemberOpen] = useState(false)
  const [addGrantOpen, setAddGrantOpen] = useState(false)
  const [confirmGrant, setConfirmGrant] = useState<{ access_group: string; unique_id: string } | null>(null)
  const [addOccOpen, setAddOccOpen] = useState(false)
  const [confirmOcc, setConfirmOcc] = useState<GroupOccupancy | null>(null)
  const removeOcc = useMutation({
    mutationFn: (occ: GroupOccupancy) => api.deleteGroupOccupancy(uid, occ.unique_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope] })
      setConfirmOcc(null)
    },
  })

  useEffect(() => {
    if (group) {
      setName(group.name)
      setKind(group.kind)
      setParent(group.parent ?? '')
      setExternalRef(group.external_ref)
      setInheritsGrants(group.inherits_grants ?? true)
    }
  }, [group])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [scope, 'groups'] })
  }
  const save = useMutation({
    mutationFn: () =>
      api.updateGroup(uid, {
        name: name.trim(),
        kind,
        external_ref: externalRef.trim(),
        parent: parent || null,
        inherits_grants: inheritsGrants,
      }),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: () => api.deleteGroup(uid),
    onSuccess: () => {
      setConfirmDelete(false)
      invalidate()
      navigate('/groups')
    },
  })
  const mergeGroup = useMergeGroup()
  const allGroups = useGroups()
  const groupOptions = (allGroups.data ?? [])
    .filter((g) => g.unique_id !== uid)
    .map((g) => ({ value: g.unique_id, label: `${g.name} · ${g.unique_id}` }))
  const removeMember = useMutation({
    mutationFn: (person: Person) =>
      api.updatePerson(person.unique_id, personBody(person, person.groups.filter((g) => g !== uid))),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [scope] }),
  })
  const removeGrant = useMutation({
    mutationFn: (grant: { access_group: string; unique_id: string }) =>
      api.deleteDirectoryGrant(grant.access_group, grant.unique_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope, 'directory-grants'] })
      queryClient.invalidateQueries({ queryKey: [scope, 'access-groups'] })
      queryClient.invalidateQueries({ queryKey: [scope, 'effective-access'] })
      setConfirmGrant(null)
    },
  })

  const node = useMemo(() => (tree.data ? findGroupNode(tree.data, uid) : null), [tree.data, uid])
  const excluded = useMemo(() => (node ? groupSubtreeIds(node) : new Set<string>()), [node])

  if (groupQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10" />
        <Skeleton className="h-48" />
        <Skeleton className="h-40" />
      </div>
    )
  }
  if (groupQuery.isError) {
    return <EmptyState title="Could not load group" hint={errText(groupQuery.error)} />
  }
  if (!group) {
    return (
      <EmptyState
        title="Group not found"
        hint={uid}
        action={
          <Button variant="ghost" size="sm" onClick={() => navigate('/groups')}>
            ← All groups
          </Button>
        }
      />
    )
  }

  const members = (persons.data ?? []).filter((p) => p.groups.includes(uid))
  // A group bound to a dry-run system is read-only (edit/delete/membership).
  const groupReadOnly = readOnlyReason(group)
  const accessGroupNames = new Map((accessGroups.data ?? []).map((g) => [g.unique_id, g.name]))
  // Grants pointing at THIS directory group, flattened with their access group.
  const grants = (allGrants.data ?? []).flatMap((row) =>
    row.grants.filter((g) => g.group === uid).map((g) => ({ ...g, access_group: row.access_group })),
  )
  const grantedAccessGroups = new Set(grants.map((g) => g.access_group))
  const hasGrantableAccessGroups = (accessGroups.data ?? []).some((g) => !grantedAccessGroups.has(g.unique_id))

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Directory" title={group.name}>
        <p className="mt-1 flex items-center gap-2 text-xs text-ostrel-ink-muted">
          <Chip
            tone={group.kind === 'organisation' ? 'gold' : 'muted'}
            label={groupKindLabel(group.kind)}
          />
          {group.external_ref && <span className="tnum">{group.external_ref}</span>}
        </p>
      </PageHeader>

      <Card>
        <div className="flex flex-col gap-4">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Edit group · <span className="tnum">{group.unique_id}</span>
          </p>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <SelectField
            label="Kind"
            value={kind}
            onChange={(v) => setKind(v as GroupKind)}
            options={GROUP_KINDS}
          />
          <GroupParentSelect
            id="edit-parent"
            tree={tree.data ?? []}
            value={parent}
            exclude={excluded}
            onChange={setParent}
          />
          <Input
            label="External ref"
            value={externalRef}
            onChange={(e) => setExternalRef(e.target.value)}
            placeholder="Optional"
          />
          <label className="flex items-center gap-2 text-xs text-ostrel-ink">
            <input
              type="checkbox"
              checked={!inheritsGrants}
              onChange={(e) => setInheritsGrants(!e.target.checked)}
              className="accent-ostrel-gold"
            />
            Require direct assignment (no inherited access)
          </label>
          {save.isError && <p className="text-xs text-ostrel-danger">{errText(save.error)}</p>}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="navy"
              disabled={save.isPending || !name.trim() || groupReadOnly !== undefined}
              title={groupReadOnly}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Saving…' : 'Save changes'}
            </Button>
            <Button
              variant="ghost"
              disabled={groupReadOnly !== undefined}
              title={groupReadOnly}
              onClick={() => setMergeOpen(true)}
            >
              Merge…
            </Button>
            <Button variant="danger" disabled={groupReadOnly !== undefined} title={groupReadOnly} onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          </div>
        </div>
      </Card>

      <Card title="Child groups">
        {!node || node.children.length === 0 ? (
          <EmptyState
            title="No child groups"
            hint="Groups created with this group as parent appear here — they inherit its access grants."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-ostrel-line">
            {node.children.map((c) => (
              <li key={c.unique_id} className="flex items-center gap-4 py-2.5">
                <span className="min-w-0 flex-1">
                  <Link
                    to={`/groups/${c.unique_id}`}
                    className="block truncate text-sm text-ostrel-ink hover:text-ostrel-accent"
                  >
                    {c.name}
                  </Link>
                  <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">
                    {c.unique_id}
                  </span>
                </span>
                <Chip
                  tone={c.kind === 'organisation' ? 'gold' : 'muted'}
                  label={groupKindLabel(c.kind)}
                />
                <span className="tnum text-[11px] text-ostrel-ink-muted">
                  {c.people_count} {c.people_count === 1 ? 'person' : 'people'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        title="Members"
        action={
          <Button
            variant="ghost"
            size="sm"
            disabled={groupReadOnly !== undefined}
            title={groupReadOnly}
            onClick={() => setAddMemberOpen(true)}
          >
            + Add member
          </Button>
        }
      >
        {persons.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-7" />
            <Skeleton className="h-7" />
            <Skeleton className="h-7" />
          </div>
        ) : persons.isError ? (
          <EmptyState title="Could not load members" hint={errText(persons.error)} />
        ) : members.length === 0 ? (
          <EmptyState
            title="No members"
            hint="Add people to this group — members inherit its access grants."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-ostrel-line">
            {members.map((m) => (
              <li key={m.unique_id} className="flex items-center gap-4 py-2.5">
                <span className="min-w-0 flex-1">
                  <Link
                    to={`/directory/${m.unique_id}`}
                    className="block truncate text-sm text-ostrel-ink hover:text-ostrel-accent"
                  >
                    {m.display_name || 'Unnamed'}
                  </Link>
                  <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">
                    {m.unique_id}
                  </span>
                </span>
                <Chip tone={personStatusTone(m.status)} label={m.status} />
                <Button
                  variant="danger"
                  size="sm"
                  disabled={removeMember.isPending || groupReadOnly !== undefined}
                  title={groupReadOnly}
                  onClick={() => removeMember.mutate(m)}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
        {removeMember.isError && (
          <p className="mt-2 text-xs text-ostrel-danger">{errText(removeMember.error)}</p>
        )}
      </Card>

      <Card
        title="Occupancies"
        action={
          <Button
            variant="ghost"
            size="sm"
            disabled={groupReadOnly !== undefined}
            title={groupReadOnly}
            onClick={() => setAddOccOpen(true)}
          >
            + Add
          </Button>
        }
      >
        {occupancies.isPending ? (
          <Skeleton className="h-7" />
        ) : occupancies.isError ? (
          <EmptyState title="Could not load occupancies" hint={errText(occupancies.error)} />
        ) : occupancies.data.length === 0 ? (
          <EmptyState
            title="No occupancies"
            hint="Place this group at a location — every member, including subgroups, occupies it too."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-ostrel-line">
            {occupancies.data.map((o) => (
              <li key={o.unique_id} className="flex items-center gap-4 py-2.5">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-ostrel-ink">
                    <LocationLink uid={o.location}>{locPaths.get(o.location) ?? o.location}</LocationLink>
                  </span>
                  <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">{o.unique_id}</span>
                </span>
                <span className="tnum text-[11px] text-ostrel-ink-muted">
                  {o.member_count} {o.member_count === 1 ? 'member' : 'members'}
                </span>
                <Chip tone={o.kind === 'resident' ? 'gold' : 'muted'} label={o.kind} />
                <Button
                  variant="danger"
                  size="sm"
                  disabled={removeOcc.isPending || groupReadOnly !== undefined}
                  title={groupReadOnly}
                  onClick={() => setConfirmOcc(o)}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        title="Access"
        action={
          hasGrantableAccessGroups ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={groupReadOnly !== undefined}
              title={groupReadOnly}
              onClick={() => setAddGrantOpen(true)}
            >
              + Grant access group
            </Button>
          ) : undefined
        }
      >
        {effective.isPending || allGrants.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : effective.isError ? (
          <EmptyState title="Could not load access" hint={errText(effective.error)} />
        ) : allGrants.isError ? (
          <EmptyState title="Could not load access grants" hint={errText(allGrants.error)} />
        ) : grants.length === 0 && effective.data.doors.length === 0 ? (
          <EmptyState
            title="No access granted"
            hint="Grant an access group — every member of this group (and groups below it) joins it."
          />
        ) : (
          <div className="flex flex-col gap-4">
            {grants.length > 0 && (
              <div>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                  Access groups
                </p>
                <ul className="flex flex-col divide-y divide-ostrel-line">
                  {grants.map((g) => (
                    <li key={g.unique_id} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                      <span className="min-w-0 flex-1">
                        <Link
                          to={`/access-groups/${g.access_group}`}
                          className="block truncate text-sm text-ostrel-ink hover:text-ostrel-accent"
                        >
                          {accessGroupNames.get(g.access_group) ?? g.access_group}
                        </Link>
                        <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">
                          {g.unique_id}
                        </span>
                      </span>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={groupReadOnly !== undefined}
                        title={groupReadOnly}
                        onClick={() => setConfirmGrant(g)}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {effective.data.doors.length > 0 && (
              <div>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                  Doors
                </p>
                <ul className="flex flex-col divide-y divide-ostrel-line">
                  {effective.data.doors.map((d) => (
                    <li key={d.door} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-ostrel-ink">{d.name}</span>
                        <span className="block truncate text-[11px] text-ostrel-ink-muted">{d.path.join(' › ')}</span>
                        {d.via.length > 0 && (
                          <span className="block truncate text-[11px] text-ostrel-ink-muted">
                            via{' '}
                            {d.via
                              .map((v) => accessGroupNames.get(v) ?? v)
                              .join(', ')}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>

      <MergeModal
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        title="Merge group"
        description="Pick another directory group to merge into. The current group is deleted and its members become members of the target group."
        source={{
          unique_id: group.unique_id,
          label: group.name,
          kind: groupKindLabel(group.kind),
        }}
        options={groupOptions}
        loading={allGroups.isPending}
        pending={mergeGroup.isPending}
        error={mergeGroup.error}
        onConfirm={(target) =>
          mergeGroup.mutate(
            { uid, target },
            {
              onSuccess: () => {
                setMergeOpen(false)
                navigate(`/groups/${target}`)
              },
            },
          )
        }
      />

      <AddMemberModal
        open={addMemberOpen}
        groupUid={uid}
        existing={new Set(members.map((m) => m.unique_id))}
        onClose={() => setAddMemberOpen(false)}
      />
      <AddAccessGrantModal
        open={addGrantOpen}
        groupUid={uid}
        taken={new Set(grants.map((g) => g.access_group))}
        onClose={() => setAddGrantOpen(false)}
      />

      <AddOccupancyModal open={addOccOpen} onClose={() => setAddOccOpen(false)} target={{ group: uid }} />

      <ConfirmDeleteModal
        open={confirmOcc !== null}
        title="Remove group occupancy"
        objectName={confirmOcc ? (locPaths.get(confirmOcc.location) ?? confirmOcc.location) : ''}
        expected={confirmOcc?.unique_id ?? ''}
        pending={removeOcc.isPending}
        error={removeOcc.isError ? errText(removeOcc.error) : null}
        confirmLabel={removeOcc.isPending ? 'Removing…' : 'Remove'}
        onConfirm={() => confirmOcc && removeOcc.mutate(confirmOcc)}
        onClose={() => setConfirmOcc(null)}
      >
        <p>
          Remove this group&apos;s occupancy? Its {confirmOcc?.member_count ?? 0} members stop occupying the location
          through the group (their direct occupancies are kept).
        </p>
      </ConfirmDeleteModal>

      <ConfirmDeleteModal
        open={confirmGrant !== null}
        title="Remove access grant"
        objectName={confirmGrant ? (accessGroupNames.get(confirmGrant.access_group) ?? confirmGrant.access_group) : ''}
        expected={confirmGrant?.unique_id ?? ''}
        pending={removeGrant.isPending}
        error={removeGrant.isError ? errText(removeGrant.error) : null}
        confirmLabel={removeGrant.isPending ? 'Removing…' : 'Remove'}
        onConfirm={() => confirmGrant && removeGrant.mutate(confirmGrant)}
        onClose={() => setConfirmGrant(null)}
      >
        <p>
          Remove the grant of{' '}
          <span className="font-medium">
            {confirmGrant ? (accessGroupNames.get(confirmGrant.access_group) ?? confirmGrant.access_group) : ''}
          </span>{' '}
          to this group? Its members stop being members of that access group.
        </p>
      </ConfirmDeleteModal>

      <ConfirmDeleteModal
        open={confirmDelete}
        title="Delete group"
        objectName={group.name}
        expected={group.unique_id}
        pending={remove.isPending}
        error={remove.isError ? errText(remove.error) : null}
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete'}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDelete(false)}
      >
        <p>
          Delete <span className="font-medium">{group.name}</span>? Its members are kept — they
          are simply detached from the group.
        </p>
      </ConfirmDeleteModal>
    </div>
  )
}
